import { ReactNode, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { getQueryFn, redirectToLogin } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { canAccessCompanySettings, canAccessPlatformSettings } from "@/lib/platformAccess";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { msg, type Msg } from "@shared/i18n";

interface AdminGuardProps {
  children: ReactNode;
}

/** Platform Settings: SaleFish Admins only. */
export function AdminGuard({ children }: AdminGuardProps) {
  return (
    <AccessGuard allow={(user) => canAccessPlatformSettings(user)} area={msg("salefishAdmin", "SaleFish Admin")}>
      {children}
    </AccessGuard>
  );
}

/** Company Settings: the company's admins and SaleFish Admins. */
export function CompanySettingsGuard({ children }: AdminGuardProps) {
  return (
    <AccessGuard allow={(user) => canAccessCompanySettings(user)} area={msg("companySettings", "Company Settings")}>
      {children}
    </AccessGuard>
  );
}

function AccessGuard({ allow, area, children }: AdminGuardProps & { allow: (user: any) => boolean; area: Msg }) {
  const t = useTranslation();
  const [, setLocation] = useLocation();
  
  /* The signed-in user AuthContext already holds is the placeholder while the
     guard's own fresh read is out. With `gcTime: 0` that read starts from
     nothing on every mount — and every Platform Settings section remounts
     this guard (the layer keys each screen on its location) — so each
     section switch used to render NOTHING for one extra /api/auth/user round
     trip before its page could even begin loading: a blank panel between the
     old section and the new one's skeleton. The fresh read still runs and
     still decides: a role that no longer passes turns into Access Denied,
     and a session that has gone (null) still redirects, the moment it
     answers — `isSuccess && !user` only sees the server's answer, because a
     placeholder is never null here. */
  const { user: signedIn } = useAuth();
  const { data: user, isSuccess } = useQuery<any>({
    queryKey: ['/api/auth/user'],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    placeholderData: signedIn ?? undefined,
  });
  
  const isAdmin = allow(user);
  
  // A settled query with no user means the session is gone: getQueryFn maps a 401
  // on /api/auth/user to null rather than throwing, so nothing will ever
  // repopulate it. Returning null on its own would strand every page behind this
  // guard on a permanently blank screen with no way out — which is how a single
  // auth failure took all fifteen admin pages down at once. Gate on isSuccess so
  // a transient network error, which also leaves `user` empty, does not bounce a
  // signed-in admin to login.
  useEffect(() => {
    if (isSuccess && !user) {
      redirectToLogin();
    }
  }, [isSuccess, user]);

  // Still resolving, or already navigating away: render nothing. The child page
  // owns the loading state, so a loader here would double up.
  if (!user) {
    return null;
  }
  
  if (!isAdmin) {
    return (
      <div className="min-h-[60dvh] flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-destructive/10">
                <ShieldAlert className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <CardTitle>{t("shellAuthAccessDenied", "Access Denied")}</CardTitle>
                <CardDescription className="mt-2">
                  {t("shellAuthNoPermissionArea", "You don't have permission to access {area}", { area: t(area) })}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setLocation('/projects')}
              className="w-full"
              data-testid="button-go-to-projects"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("shellAuthGoToProjects", "Go to Projects")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return <>{children}</>;
}
